import { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

/**
 * Was der Provider bereitstellt. Die Datei ist JavaScript; der Typ steht hier,
 * damit TypeScript-Aufrufer (`useAnmeldeFreigabe`) nicht `never` erben.
 *
 * @typedef {Object} SiteConfigWert
 * @property {Record<string, unknown>} config  Antwort von `/api/public/site-config`
 * @property {boolean} loading
 * @property {unknown} error
 * @property {() => Promise<void>} refreshConfig
 * @property {(area: string) => boolean} isAreaPublic
 * @property {(area: string) => boolean} isAreaEnabled
 * @property {(area: string) => boolean} isAreaProtected
 * @property {boolean} registrationContactEnabled
 * @property {string} registrationContactEmail
 * @property {boolean} isMaintenanceMode
 * @property {string} siteName
 * @property {string} siteTagline
 * @property {string} primaryColor
 * @property {string | null} logoUrl
 * @property {string | null} logoDunkelUrl
 * @property {string | null} iconUrl
 * @property {string | null} faviconUrl
 * @property {boolean} hasCustomLogo
 * @property {boolean} hasCustomLogoDunkel
 * @property {boolean} hasCustomIcon
 * @property {boolean} hasCustomFavicon
 */

/** @type {import('react').Context<SiteConfigWert | null>} */
const SiteConfigContext = createContext(/** @type {SiteConfigWert | null} */ (null));

// Vorgaben für die Darstellung, solange die Antwort fehlt. Die Schalter
// `login_enabled`/`registration_enabled` stehen bewusst NICHT hier: Eine
// Vorgabe `true` öffnete die Registrierung, sobald `/api/public/site-config`
// scheitert. Gelesen werden sie nur über `useAnmeldeFreigabe`, das `loading`
// und `error` mitprüft.
const DEFAULT_CONFIG = {
  registration_contact_enabled: false,
  registration_contact_email: '',
  areas: {
    portfolio: 'authenticated',
    community: 'public',
    // Hard-Split: news lebt in paperball.news.
  },
  public_landing_page: 'leaderboard',
  site_name: 'Paperball',
  site_tagline: 'Analyze. Compare. Share.',
  maintenance_mode: false,
  maintenance_message: null,
  primary_color: 'var(--info)',
  logo_url: null,
  logo_dunkel_url: null,
  icon_url: null,
  favicon_url: null,
  has_custom_logo: false,
  has_custom_logo_dunkel: false,
  has_custom_icon: false,
  has_custom_favicon: false,
};

/**
 * Die Antwort von `/api/public/site-config`, die eine Server-Seite (Next.js-
 * Phase 2) beim Backend geholt hat. Damit rendern Server und Browser denselben
 * ersten Stand — mit Anmelde-Schaltern, Bereichen und Wartungsmodus —, statt
 * dass der Server die Vorgaben oben zeigt und der Knopf „Kostenlos starten“
 * erst im Browser erscheint. Der Provider fragt beim Einhängen trotzdem neu.
 */
let vorgelegteConfig = null;

export function legeSiteConfigVor(config) {
  vorgelegteConfig = config;
}

export function SiteConfigProvider({ children }) {
  const [config, setConfig] = useState(() => vorgelegteConfig ?? DEFAULT_CONFIG);
  const [loading, setLoading] = useState(() => vorgelegteConfig === null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadConfig();
  }, []);

  // Dynamisches Favicon setzen
  useEffect(() => {
    if (config.has_custom_favicon && config.favicon_url) {
      // Finde oder erstelle das favicon link-Element
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = config.favicon_url;
    }
  }, [config.has_custom_favicon, config.favicon_url]);

  const loadConfig = async () => {
    try {
      const response = await api.get('/api/public/site-config');
      setConfig(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to load site config:', err);
      setError(err);
      // Behalte Default-Config bei Fehler
    } finally {
      setLoading(false);
    }
  };

  const refreshConfig = () => loadConfig();

  // Hilfsfunktionen
  const isAreaPublic = (area) => {
    return config.areas?.[area] === 'public';
  };

  const isAreaEnabled = (area) => {
    return config.areas?.[area] !== 'disabled';
  };

  const isAreaProtected = (area) => {
    return config.areas?.[area] === 'authenticated';
  };

  const value = {
    config,
    loading,
    error,
    refreshConfig,
    
    // Convenience
    isAreaPublic,
    isAreaEnabled,
    isAreaProtected,
    
    // Shortcuts
    registrationContactEnabled: config.registration_contact_enabled,
    registrationContactEmail: config.registration_contact_email,
    isMaintenanceMode: config.maintenance_mode,
    siteName: config.site_name,
    siteTagline: config.site_tagline,
    primaryColor: config.primary_color,
    
    // Branding Bilder
    logoUrl: config.logo_url,
    logoDunkelUrl: config.logo_dunkel_url,
    iconUrl: config.icon_url,
    faviconUrl: config.favicon_url,
    hasCustomLogo: config.has_custom_logo,
    hasCustomLogoDunkel: config.has_custom_logo_dunkel,
    hasCustomIcon: config.has_custom_icon,
    hasCustomFavicon: config.has_custom_favicon,
  };

  return (
    <SiteConfigContext.Provider value={value}>
      {children}
    </SiteConfigContext.Provider>
  );
}

/** @returns {SiteConfigWert} */
export function useSiteConfig() {
  const context = useContext(SiteConfigContext);
  if (!context) {
    throw new Error('useSiteConfig must be used within a SiteConfigProvider');
  }
  return context;
}

export default useSiteConfig;
